import PropertyVideo from '@components/PropertyDetails/PropertyVideo'
import {
  LISTING_STATUS,
  LISTING_TYPE,
  PROPERTY_RESULTS_PATH,
} from '@constants/property'
import { useSiteSettingsContext } from '@context/SiteSettingsContext'
import { filterHrefs, getBeforeYouBidBPIURL } from '@lib/helpers'
import { Col, Container, Row } from '@styles/global.styled'
import Breadcrumbs from '../global/Breadcrumbs'
import Auction from './Auction'
import ContactNav from './ContactNav'
import { formatListingData } from './helper'
import HelpfulLinks from './HelpfulLinks'
import Inspections from './Inspections'
import KeyDetails from './KeyDetails'
import PropertyPhoto from './Photo'
import PropertyAddress from './PropertyAddress'
import * as S from './PropertyDetails.styled'
import PropertyFeatures from './PropertyFeatures'
import PropertyInfo from './PropertyInfo'
import PropertyMobileNav from './PropertyMobileNav'
import PropertyPrice from './PropertyPrice'
import SuburbProfile from './SuburbProfile'
import dynamic from 'next/dynamic'

const Map = dynamic(() => import('@global/Map/SimpleMap'), {
  ssr: false,
})

const getPropertyBreadcrumbs = (listing) => {
  const { listing_type, address_suburb, address_display_string } = listing

  let breadcrumbs = []

  if (listing_type === LISTING_TYPE.SALE) {
    if (listing.status === 'sold') {
      breadcrumbs.push({
        label: 'Sold',
        url: PROPERTY_RESULTS_PATH.sold,
      })
    } else {
      breadcrumbs.push({
        label: 'For Sale',
        url: PROPERTY_RESULTS_PATH.sale,
      })
    }
  } else {
    breadcrumbs.push({
      label: 'For Lease',
      url: PROPERTY_RESULTS_PATH.lease,
    })
  }

  breadcrumbs = breadcrumbs.concat([
    {
      label: address_suburb,
      url: `${PROPERTY_RESULTS_PATH[listing_type]}?property_class=residential&listing_type=${listing_type}&search_view=location&order_by=-created&save_search=false&address_suburb=${address_suburb}${listing.status === LISTING_STATUS.SOLD ? `&status=${listing.status}` : ''}`,
    },
    {
      label: address_display_string,
      url: '/',
    },
  ])

  return breadcrumbs
}

const PropertyDetails = (props) => {
  const siteContext = useSiteSettingsContext()

  const {
    data,
    similarProperties,
    suburbData,
    backToResultsLink,
    infoPanels,
    footerCTA,
    offmarketUnlocked,
    imgPath = '',
  } = props

  const listing = { ...data, ...formatListingData(data) }
  const breadcrumbs = getPropertyBreadcrumbs(listing)

  /* helpfulLinks section */
  let helpfulLinks = []
  if (
    listing.is_before_you_bid_bpi_eligible &&
    listing.office.before_you_bid_bpi != '' &&
    listing.office.before_you_bid_bpi != null &&
    listing.agents[0]
  ) {
    helpfulLinks.push({
      type: 'link',
      label: 'Request a Building & Pest Inspection Report',
      link: getBeforeYouBidBPIURL(listing),
      target: '_blank',
    })
  }
  if (
    siteContext?.settings?.contentsettings &&
    siteContext?.settings?.contentsettings?.due_diligence_checklist &&
    helpfulLinks &&
    listing.listingType == 'sale'
  ) {
    helpfulLinks.push({
      type: 'document',
      label: 'Due Diligence Checklist',
      link: siteContext.settings.contentsettings.due_diligence_checklist,
      target: '_self',
    })
  }
  // external links
  const links = filterHrefs(
    data,
    'external,interactivefloorplans,onlineauctions'
  )
  if (links.external) {
    helpfulLinks = helpfulLinks.concat(
      links.external.map((it) => ({
        type: 'link',
        link: it,
        target: '_blank',
      }))
    )
  }
  if (links.interactivefloorplans) {
    helpfulLinks = helpfulLinks.concat(
      links.interactivefloorplans.map((it) => ({
        label: 'Interactive Floorplan',
        type: 'link',
        link: it,
        target: '_blank',
      }))
    )
  }
  // online auction links
  if (links.onlineauctions) {
    helpfulLinks = helpfulLinks.concat(
      links.onlineauctions.map((it) => ({
        label: 'Online auction',
        type: 'link',
        link: it,
        target: '_blank',
      }))
    )
  }
  if (listing.liveAuctionUrl) {
    helpfulLinks.push({
      type: 'link',
      label: 'View Live Auction',
      link: getBeforeYouBidBPIURL(listing),
      target: '_blank',
    })
  }
  if (listing.liveAuctionRegisterUrl) {
    helpfulLinks.push({
      type: 'link',
      label: 'Register to Bid',
      link: listing.liveAuctionRegisterUrl,
      target: '_blank',
    })
  }

  return (
    <>
      <Container>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <PropertyPhoto
          listing={listing}
          offmarketUnlocked={offmarketUnlocked}
        />
        <Row>
          <Col lg={8}>
            <S.PropertyDetails>
              <PropertyAddress
                listing={listing}
                offmarketUnlocked={offmarketUnlocked}
              />
              <KeyDetails
                listing={listing}
                offmarketUnlocked={offmarketUnlocked}
              />
              <PropertyPrice listing={listing} />
              <ContactNav
                agents={listing.agents}
                office={listing.office}
                mobile
                listing={listing}
              />
              <PropertyInfo
                heading={listing.headline}
                description={listing.description}
              />
              {listing?.hrefs?.some((href) => href.link_type === 'video') && (
                <PropertyVideo videos={listing?.hrefs} />
              )}
              <PropertyFeatures
                features={listing?.listingFeatures?.filter((it) =>
                  ['livingAreas', 'toilets'].includes(it.name)
                    ? it.value
                    : it.name
                )}
              />
              <Auction listing={listing} />
              <Inspections listing={listing} />
              {![LISTING_STATUS.SOLD, LISTING_STATUS.LEASED].includes(
                listing.status
              ) &&
                listing?.location?.coordinates?.length > 0 &&
                listing.address_display && (
                  <Map coordinates={listing.location.coordinates} />
                )}
              {![LISTING_STATUS.SOLD, LISTING_STATUS.LEASED].includes(
                listing.status
              ) && <HelpfulLinks helpfulLinks={helpfulLinks} />}
            </S.PropertyDetails>
          </Col>
          <Col lg={4}>
            <ContactNav
              agents={listing.agents}
              office={listing.office}
              listing={listing}
            />
          </Col>
        </Row>
      </Container>

      {listing?.suburb_profile?.suburb && (
        <SuburbProfile
          suburb={listing.suburb_profile}
          state={listing.address_state}
          postcode={listing.address_postcode}
          suburbHeadingAs='h2'
        />
      )}

      <PropertyMobileNav listing={listing} />
    </>
  )
}

export default PropertyDetails
